import { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { apiGet } from '../../../lib/api';

interface Coupon {
  id: string;
  code: string;
  status: string;
  discountType: string;
  discountValue: number;
  description: string | null;
  expiresAt: string | null;
  issuedAt: string;
  usedAt: string | null;
}

interface LoyaltyCouponsProps {
  title?: string;
  maxItems?: number;
  showExpired?: boolean;
}

function LoginCta() {
  return (
    <div className="loyalty-card p-8 text-center">
      <div className="text-4xl mb-3 opacity-50">&#127915;</div>
      <h2 className="loyalty-heading text-xl mb-2 text-[var(--color-text)]">
        Login to view your coupons
      </h2>
      <p className="text-[var(--color-text-muted)] text-sm mb-4">
        Sign in to see your active coupons and discount codes.
      </p>
      <a
        href="/login"
        className="inline-block px-6 py-2 rounded-lg bg-[var(--color-primary)] text-white font-semibold text-sm hover:opacity-90 transition-opacity"
      >
        Sign In
      </a>
    </div>
  );
}

function CouponCard({ coupon }: { coupon: Coupon }) {
  const [copied, setCopied] = useState(false);
  const isExpired = coupon.expiresAt ? new Date(coupon.expiresAt) < new Date() : false;
  const isUsed = coupon.status === 'used';
  const isActive = !isExpired && !isUsed;

  function handleCopy() {
    navigator.clipboard.writeText(coupon.code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  const discountLabel =
    coupon.discountType === 'percent'
      ? `${coupon.discountValue}% OFF`
      : coupon.discountType === 'fixed'
      ? `$${coupon.discountValue} OFF`
      : 'FREE';

  return (
    <div
      className={`loyalty-card overflow-hidden flex ${
        isActive ? '' : 'opacity-60'
      }`}
    >
      {/* Left accent */}
      <div
        className={`w-2 flex-shrink-0 ${
          isActive ? 'bg-[var(--color-primary)]' : isUsed ? 'bg-gray-300' : 'bg-red-300'
        }`}
      />

      <div className="flex-1 p-4 flex items-center gap-4">
        {/* Discount badge */}
        <div className="flex-shrink-0 text-center">
          <div className="text-lg font-bold text-[var(--color-primary)]">{discountLabel}</div>
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <code className="text-sm font-mono font-bold text-[var(--color-text)] bg-[var(--color-surface)] px-2 py-0.5 rounded">
              {coupon.code}
            </code>
            {isActive && (
              <button
                onClick={handleCopy}
                className="text-[10px] font-semibold px-2 py-0.5 rounded border border-[var(--color-surface-border)] text-[var(--color-text-muted)] hover:text-[var(--color-text)] transition-colors"
              >
                {copied ? 'Copied!' : 'Copy'}
              </button>
            )}
          </div>
          {coupon.description && (
            <p className="text-xs text-[var(--color-text-muted)] mt-1 truncate">{coupon.description}</p>
          )}
          <div className="text-[10px] text-[var(--color-text-muted)] mt-1">
            {isUsed
              ? `Used ${coupon.usedAt ? new Date(coupon.usedAt).toLocaleDateString() : ''}`
              : isExpired
              ? `Expired ${coupon.expiresAt ? new Date(coupon.expiresAt).toLocaleDateString() : ''}`
              : coupon.expiresAt
              ? `Expires ${new Date(coupon.expiresAt).toLocaleDateString()}`
              : 'No expiry'}
          </div>
        </div>

        {/* Status */}
        <span
          className={`flex-shrink-0 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${
            isActive
              ? 'bg-emerald-100 text-emerald-700'
              : isUsed
              ? 'bg-gray-100 text-gray-500'
              : 'bg-red-100 text-red-600'
          }`}
        >
          {isUsed ? 'Used' : isExpired ? 'Expired' : 'Active'}
        </span>
      </div>
    </div>
  );
}

interface LoyaltyFullData {
  enrolled: boolean;
  coupons: Coupon[];
}

export function LoyaltyCouponsBlock({ props }: { props: LoyaltyCouponsProps }) {
  const { isAuthenticated } = useAuth();
  const title = props.title ?? 'My Coupons';
  const maxItems = props.maxItems ?? 10;
  const showExpired = props.showExpired ?? false;

  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    setLoading(true);
    apiGet<LoyaltyFullData>('/customer/loyalty')
      .then((res) => {
        if (!cancelled) setCoupons(res.coupons ?? []);
      })
      .catch(() => { if (!cancelled) setCoupons([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  if (loading) {
    return (
      <div className="loyalty-card p-6 animate-pulse">
        <div className="h-5 w-32 bg-[var(--color-surface-border)] rounded mb-4" />
        <div className="space-y-3">
          {[1, 2].map((i) => (
            <div key={i} className="h-16 bg-[var(--color-surface-border)] rounded" />
          ))}
        </div>
      </div>
    );
  }

  if (!isAuthenticated) return <LoginCta />;

  const filtered = showExpired
    ? coupons.slice(0, maxItems)
    : coupons.filter((c) => c.status === 'issued').slice(0, maxItems);

  return (
    <div>
      <h2 className="loyalty-heading text-lg text-[var(--color-text)] mb-4">{title}</h2>
      {filtered.length > 0 ? (
        <div className="space-y-3">
          {filtered.map((coupon) => (
            <CouponCard key={coupon.id} coupon={coupon} />
          ))}
        </div>
      ) : (
        <div className="loyalty-card p-6 text-center text-[var(--color-text-muted)] text-sm">
          No coupons yet. Redeem rewards to earn coupon codes!
        </div>
      )}
    </div>
  );
}
